import { useQuery } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from '../auth/AuthAllapot.tsx';
import { supabase } from '../lib/supabase.ts';
import { tarIr, tarOlvas } from '../lib/tarolo.ts';
import type { TagSzerep } from '../lib/tipusok.ts';
import { Betolto, Gomb, HibaUzenet, Ures } from '../ui/elemek.tsx';

export interface IrodaRovid { id: string; nev: string; slug: string }
export interface AktivIrodaErtek {
  irodak: IrodaRovid[]; aktivId: string | null; beallit(id: string): void; szerep: TagSzerep | null;
  jogok: { vezeto: boolean; super: boolean };
}
const Ctx = createContext<AktivIrodaErtek | null>(null);
const KULCS = 'aktivIroda';

export function AktivIrodaSzolgaltato({ children }: { children: ReactNode }) {
  const { profil, session, kijelentkezes } = useAuth();
  const userId = session?.user.id ?? '';
  const [kilepesHiba, setKilepesHiba] = useState<unknown>(null);
  const [valasztott, setValasztott] = useState<string | null>(() => tarOlvas(KULCS));

  const irodak = useQuery({
    queryKey: ['irodak'],
    queryFn: async () => {
      const { data, error } = await supabase.from('offices').select('id, nev, slug').order('nev');
      if (error) throw error;
      return data satisfies IrodaRovid[];
    },
  });
  const tagsagok = useQuery({
    queryKey: ['tagsagok', userId],
    queryFn: async () => {
      const { data, error } = await supabase.from('office_members').select('office_id, szerep').eq('user_id', userId).eq('aktiv', true);
      if (error) throw error;
      return data;
    },
    enabled: userId !== '',
  });

  const beallit = useCallback((id: string) => { tarIr(KULCS, id); setValasztott(id); }, []);

  const lista = irodak.data;
  const tagsag = tagsagok.data;
  const ertek = useMemo<AktivIrodaErtek | null>(() => {
    if (!lista || !tagsag || !profil) return null;
    const aktivId = lista.find((i) => i.id === valasztott)?.id ?? lista[0]?.id ?? null;
    const szerep = tagsag.find((t) => t.office_id === aktivId)?.szerep ?? null;
    return {
      irodak: lista, aktivId, beallit, szerep,
      jogok: { super: profil.super_admin, vezeto: profil.super_admin || szerep === 'vezeto' },
    };
  }, [lista, tagsag, profil, valasztott, beallit]);

  if (irodak.error || tagsagok.error) return <HibaUzenet hiba={irodak.error ?? tagsagok.error} />;
  if (!ertek) return <Betolto />;
  if (ertek.irodak.length === 0 && !ertek.jogok.super) {
    return (
      <div className="mx-auto max-w-lg p-5">
        <Ures cim="Nincs hozzárendelt iroda" szoveg="Még nem vagy egyik irodához sem hozzárendelve. Szólj az irodavezetődnek.">
          <HibaUzenet hiba={kilepesHiba} />
          <Gomb valtozat="masodlagos" onClick={() => { setKilepesHiba(null); kijelentkezes().catch(setKilepesHiba); }}>Kijelentkezés</Gomb>
        </Ures>
      </div>
    );
  }
  return <Ctx.Provider value={ertek}>{children}</Ctx.Provider>;
}

export function useAktivIroda(): AktivIrodaErtek {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAktivIroda csak AktivIrodaSzolgaltato alatt használható.');
  return v;
}
